import { useMemo, useState, type MouseEvent as ReactMouseEvent } from "react";
import { useQuery } from "@tanstack/react-query";

import {
  fetchDemoCoverageClassify,
  fetchDemoCoveragePoints,
  fetchDemoCoverageSummary,
  fetchDemoSampleNext,
  type DemoV3Classification,
  type DemoV3CoverageStatus,
} from "../../../api/demoV3";

/**
 * Coverage map (VERTICAL_DEMOS_V2 D4): the aero-database campaign envelope as an
 * alpha x beta scatter at one elevator slice. Sampled points vs punched holes,
 * click-anywhere-to-classify (interior / on_hull / extrapolated + thinness +
 * 3 nearest samples), and the tiered "sample next" inverse ranking.
 *
 * Hand-rolled SVG — no chart deps; colors resolve through the --v5-* tokens in
 * thread-v5.css.
 */

const VIEW_W = 420;
const VIEW_H = 260;
const PAD = { left: 34, right: 12, top: 10, bottom: 26 };

export const COVERAGE_STATUS_LABELS: Record<DemoV3CoverageStatus, string> = {
  interior: "Interior",
  on_hull: "On hull",
  extrapolated: "Extrapolated",
};

function statusClass(status: DemoV3CoverageStatus): string {
  return status === "interior" ? "is-interior" : status === "on_hull" ? "is-on-hull" : "is-extrapolated";
}

function fmt(value: number, digits = 1): string {
  return Number.isFinite(value) ? value.toFixed(digits) : "—";
}

export function CoverageMapCard({
  projectId,
  state,
}: {
  projectId: string;
  branch?: string;
  state?: Record<string, unknown>;
}) {
  const initialElevator = typeof state?.elevator === "number" ? (state.elevator as number) : 0;
  const [elevator, setElevator] = useState<number>(initialElevator);
  const [showSampleNext, setShowSampleNext] = useState(false);
  const [classification, setClassification] = useState<DemoV3Classification | null>(null);
  const [classifying, setClassifying] = useState(false);

  const summaryQuery = useQuery({
    queryKey: ["demo-v3", projectId, "coverage-summary"],
    queryFn: () => fetchDemoCoverageSummary(projectId),
    staleTime: Infinity,
    retry: false,
  });
  const pointsQuery = useQuery({
    queryKey: ["demo-v3", projectId, "coverage-points", elevator],
    queryFn: () => fetchDemoCoveragePoints(projectId, elevator),
    staleTime: Infinity,
    retry: false,
    enabled: summaryQuery.isSuccess,
  });
  const sampleNextQuery = useQuery({
    queryKey: ["demo-v3", projectId, "sample-next"],
    queryFn: () => fetchDemoSampleNext(projectId, 8),
    staleTime: Infinity,
    retry: false,
    enabled: showSampleNext,
  });

  const summary = summaryQuery.data;
  const alphaAxis = summary?.axes.alpha;
  const betaAxis = summary?.axes.beta;

  // Data<->pixel transforms with half-a-grid-step padding so hull points are visible.
  const scale = useMemo(() => {
    if (!alphaAxis || !betaAxis) return null;
    const dx = 1.0;
    const dy = 2.0;
    const x0 = alphaAxis.min - dx;
    const x1 = alphaAxis.max + dx;
    const y0 = betaAxis.min - dy;
    const y1 = betaAxis.max + dy;
    const px = (alpha: number) => PAD.left + ((alpha - x0) / (x1 - x0)) * (VIEW_W - PAD.left - PAD.right);
    const py = (beta: number) => VIEW_H - PAD.bottom - ((beta - y0) / (y1 - y0)) * (VIEW_H - PAD.top - PAD.bottom);
    const invX = (x: number) => x0 + ((x - PAD.left) / (VIEW_W - PAD.left - PAD.right)) * (x1 - x0);
    const invY = (y: number) => y0 + ((VIEW_H - PAD.bottom - y) / (VIEW_H - PAD.top - PAD.bottom)) * (y1 - y0);
    return { px, py, invX, invY };
  }, [alphaAxis, betaAxis]);

  // Holes at this slice = the summary lattice minus the sampled points.
  const holes = useMemo(() => {
    if (!summary || !pointsQuery.data) return [];
    const sampled = new Set(pointsQuery.data.points.map((p) => `${p.alpha}|${p.beta}`));
    const out: Array<{ alpha: number; beta: number }> = [];
    for (const alpha of summary.axes.alpha.values) {
      for (const beta of summary.axes.beta.values) {
        if (!sampled.has(`${alpha}|${beta}`)) out.push({ alpha, beta });
      }
    }
    return out;
  }, [summary, pointsQuery.data]);

  const handlePlotClick = (event: ReactMouseEvent<SVGSVGElement>) => {
    if (!scale) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const sx = rect.width > 0 ? VIEW_W / rect.width : 1;
    const sy = rect.height > 0 ? VIEW_H / rect.height : 1;
    const alpha = Math.round(scale.invX((event.clientX - rect.left) * sx) * 10) / 10;
    const beta = Math.round(scale.invY((event.clientY - rect.top) * sy) * 10) / 10;
    setClassifying(true);
    fetchDemoCoverageClassify(projectId, { alpha, beta, elevator })
      .then(setClassification)
      .catch(() => setClassification(null))
      .finally(() => setClassifying(false));
  };

  if (summaryQuery.isLoading) {
    return <div className="v5-demo-card is-loading" data-tour="v5-coverage-map">Loading coverage…</div>;
  }
  if (summaryQuery.isError || !summary) {
    return (
      <div className="v5-demo-card is-empty" data-tour="v5-coverage-map">
        <strong>Coverage map</strong>
        <span>No coverage assets for this project</span>
      </div>
    );
  }

  return (
    <div className="v5-demo-card v5-coverage-map" data-tour="v5-coverage-map" data-testid="v5-coverage-map">
      <div className="v5-demo-toolbar">
        <span className="v5-demo-kicker">
          δe slice
        </span>
        {summary.axes.elevator.values.map((de) => (
          <button
            key={de}
            type="button"
            className={`v5-chip v5-demo-slice-chip ${de === elevator ? "is-green" : ""}`}
            onClick={() => { setElevator(de); setClassification(null); }}
            data-testid={`v5-coverage-slice-${de}`}
          >
            {de}°
          </button>
        ))}
        <button
          type="button"
          className={`v5-chip v5-demo-toggle ${showSampleNext ? "is-green" : ""}`}
          onClick={() => setShowSampleNext((v) => !v)}
          data-testid="v5-coverage-sample-next-toggle"
        >
          Sample next
        </button>
      </div>

      <div className="v5-demo-card-body">
        <svg
          viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
          className="v5-demo-chart v5-coverage-plot"
          role="img"
          aria-label={`Coverage scatter: alpha vs beta at elevator ${elevator} deg — click to classify`}
          onClick={handlePlotClick}
          data-testid="v5-coverage-map-svg"
        >
          {scale ? (
            <>
              {/* axes */}
              <line className="v5-demo-axis" x1={PAD.left} y1={VIEW_H - PAD.bottom} x2={VIEW_W - PAD.right} y2={VIEW_H - PAD.bottom} />
              <line className="v5-demo-axis" x1={PAD.left} y1={PAD.top} x2={PAD.left} y2={VIEW_H - PAD.bottom} />
              <text className="v5-demo-axis-label" x={(PAD.left + VIEW_W - PAD.right) / 2} y={VIEW_H - 6}>α (deg)</text>
              <text className="v5-demo-axis-label" x={10} y={(PAD.top + VIEW_H - PAD.bottom) / 2} transform={`rotate(-90 10 ${(PAD.top + VIEW_H - PAD.bottom) / 2})`}>β (deg)</text>
              {[summary.axes.alpha.min, 0, summary.axes.alpha.max].map((a) => (
                <text key={`xt${a}`} className="v5-demo-tick" x={scale.px(a)} y={VIEW_H - PAD.bottom + 12}>{a}</text>
              ))}
              {[summary.axes.beta.min, 0, summary.axes.beta.max].map((b) => (
                <text key={`yt${b}`} className="v5-demo-tick is-y" x={PAD.left - 4} y={scale.py(b) + 3}>{b}</text>
              ))}
              {/* punched holes (never flown at this slice) */}
              {holes.map((h) => (
                <rect
                  key={`h${h.alpha}|${h.beta}`}
                  className="v5-coverage-hole"
                  x={scale.px(h.alpha) - 2.4}
                  y={scale.py(h.beta) - 2.4}
                  width={4.8}
                  height={4.8}
                />
              ))}
              {/* sampled points */}
              {(pointsQuery.data?.points ?? []).map((p) => (
                <circle
                  key={`p${p.alpha}|${p.beta}`}
                  className="v5-coverage-sample"
                  cx={scale.px(p.alpha)}
                  cy={scale.py(p.beta)}
                  r={2.2}
                />
              ))}
              {/* classify result: nearest links + query marker */}
              {classification ? (
                <g className="v5-coverage-query">
                  {classification.nearest.map((n, i) => (
                    <line
                      key={`n${i}`}
                      className="v5-coverage-nearest-link"
                      x1={scale.px(classification.query.alpha)}
                      y1={scale.py(classification.query.beta)}
                      x2={scale.px(n.alpha)}
                      y2={scale.py(n.beta)}
                    />
                  ))}
                  <circle
                    className={`v5-coverage-query-marker ${statusClass(classification.status)}`}
                    cx={scale.px(classification.query.alpha)}
                    cy={scale.py(classification.query.beta)}
                    r={5}
                  />
                </g>
              ) : null}
            </>
          ) : null}
        </svg>

        <aside className="v5-demo-side">
          {classification ? (
            <div className="v5-coverage-verdict" data-testid="v5-coverage-classify">
              <span className={`v5-chip v5-demo-status-chip ${statusClass(classification.status)}`}>
                {COVERAGE_STATUS_LABELS[classification.status]}
              </span>
              <dl className="v5-demo-kv">
                <div><dt>Query</dt><dd>α {fmt(classification.query.alpha)}°, β {fmt(classification.query.beta)}°, δe {fmt(classification.query.elevator)}°</dd></div>
                <div><dt>Thinness</dt><dd>{fmt(classification.thinness, 2)}× median spacing</dd></div>
                <div><dt>Hull dist</dt><dd>{fmt(classification.hull_distance, 3)}</dd></div>
              </dl>
              <span className="v5-demo-kicker">Nearest samples</span>
              <ul className="v5-demo-list">
                {classification.nearest.map((n, i) => (
                  <li key={i}>α {fmt(n.alpha)} β {fmt(n.beta)} δe {fmt(n.elevator)} · d {fmt(n.distance, 3)}</li>
                ))}
              </ul>
            </div>
          ) : (
            <div className="v5-demo-hint" data-testid="v5-coverage-hint">
              {classifying ? "Classifying…" : "Click anywhere on the map to classify: interior, on-hull or extrapolated — and how thin the data is there."}
            </div>
          )}

          {showSampleNext ? (
            <div className="v5-coverage-sample-next" data-testid="v5-coverage-sample-next">
              <span className="v5-demo-kicker">Sample next (worst coverage first)</span>
              {sampleNextQuery.isLoading ? <span className="v5-demo-hint">Ranking…</span> : null}
              <ol className="v5-demo-list is-ranked">
                {(sampleNextQuery.data?.items ?? []).map((item) => (
                  <li key={item.rank}>
                    <span className={`v5-demo-dot ${statusClass(item.status)}`} aria-hidden="true" />
                    α {fmt(item.alpha)} β {fmt(item.beta)} δe {fmt(item.elevator)}
                    <em>{COVERAGE_STATUS_LABELS[item.status]} · {fmt(item.thinness, 2)}×</em>
                  </li>
                ))}
              </ol>
            </div>
          ) : null}

          <footer className="v5-demo-footnote">
            {summary.n_samples} sampled · {summary.n_holes} holes · {summary.hole_predicates.length} documented never-flown predicates
          </footer>
        </aside>
      </div>
    </div>
  );
}
